import {
  TClaimedPromoPrizeReward,
  TUnclaimedPromoPrizeReward,
} from '@daymade/api-contracts';
import {
  MAIN_PRIZE_STRUCTURE_CODE,
  PRIZE_WIN_VALIDITY_IN_DAYS,
} from '@daymade/common-lib/constants';
import AboutUsModule from 'components/about-us/AboutUsModule';
import AboutUsSiteContainer from 'components/about-us/AboutUsSiteContainer';
import BackButton from 'components/BackButton';
import { ClaimedPrize } from 'components/dashboard/your-wins/ClaimedPrize';
import { UnclaimedPrize } from 'components/dashboard/your-wins/UnclaimedPrize';
import MainLayout from 'components/layout/MainLayout';
import NothingHereMessage from 'components/NothingHereMessage';
import PageTitle from 'components/PageTitle';
import PlainLoadingSpinner from 'components/PlainLoadingSpinner';
import { isUnclaimedPromoPrizeReward } from 'constants/WinType';
import { useAuthRedirectEffect } from 'hooks/account/useAuthRedirect';
import { useUIStore } from 'hooks/stores/useUIStore';
import { useUserStore } from 'hooks/stores/useUserStore';
import { observer } from 'mobx-react-lite';
import { NextSeo } from 'next-seo';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { TClaimedPrizeWin, TUnclaimedPrizeWin } from 'stores/UserStore';
import { ASSET_PREFIX } from 'utils/constants';
import styles from './my-wins.module.css';

type WinType = 'unclaimed' | 'claimed';
type WinTypeSelection = { title: string; subtitle?: string; type: WinType };

const winTypes: WinTypeSelection[] = [
  {
    title: 'Unclaimed Wins',
    subtitle: `All prizes and treats must be claimed within ${PRIZE_WIN_VALIDITY_IN_DAYS} days of being won.`,

    type: 'unclaimed',
  },
  {
    title: 'Claimed Wins',
    subtitle:
      'We’ll be in touch via email within 14 working days to get you your prize. Treats are awarded instantly.',
    type: 'claimed',
  },
];

const MyWins = observer(() => {
  useAuthRedirectEffect(
    window.location.pathname + window.location.search + window.location.hash,
  );
  const {
    initialized,
    currentUser,
    unclaimedPrizeWins,
    claimedPrizeWins,
    unclaimedPromoPrizeRewards,
    claimedPromoPrizeRewards,
    getPrizeWinsAndRewards,
  } = useUserStore();
  const { getAccountLink } = useUIStore();
  const [selected, setSelected] = useState<WinTypeSelection>(winTypes[0]);
  const [allUnclaimed, setAllUnclaimed] = useState<
    (TUnclaimedPromoPrizeReward | TUnclaimedPrizeWin)[]
  >([]);
  const [allClaimed, setAllClaimed] = useState<
    (TClaimedPromoPrizeReward | TClaimedPrizeWin)[]
  >([]);
  const [unexpiredUnclaimedLength, setUnexpiredUnclaimedLength] =
    useState<number>();

  useEffect(() => {
    if (
      Array.isArray(unclaimedPrizeWins) &&
      Array.isArray(unclaimedPromoPrizeRewards)
    ) {
      // Scroll to anchor when unclaimed prizes have loaded
      const hash = window.location.hash.slice(1);
      if (hash) {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }

      if (
        unclaimedPrizeWins.length > 0 ||
        unclaimedPromoPrizeRewards.length > 0
      ) {
        const unclaimedPrizeAndPromo = [
          ...unclaimedPrizeWins,
          ...unclaimedPromoPrizeRewards,
        ];

        const unclaimed = unclaimedPrizeAndPromo.sort(
          (a, b) =>
            new Date(b.expiresAt || b.createdAt).getTime() -
            new Date(a.expiresAt || a.createdAt).getTime(),
        );

        const unexpired = unclaimedPrizeAndPromo.filter(
          (win) =>
            'expiresAt' in win && win.expiresAt && win.expiresAt > new Date(),
        ).length;

        setAllUnclaimed(unclaimed);
        setUnexpiredUnclaimedLength(unexpired);
      } else {
        switchToggle(winTypes[1]);
      }
    }
  }, [unclaimedPrizeWins, unclaimedPromoPrizeRewards]);

  useEffect(() => {
    if (
      Array.isArray(claimedPrizeWins) &&
      Array.isArray(claimedPromoPrizeRewards)
    ) {
      if (claimedPrizeWins.length > 0 || claimedPromoPrizeRewards.length > 0) {
        const claimedPrizeAndPromo = [
          ...claimedPrizeWins,
          ...claimedPromoPrizeRewards,
        ];

        const claimed = claimedPrizeAndPromo.sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );

        setAllClaimed(claimed);
      }
    }
  }, [claimedPrizeWins, claimedPromoPrizeRewards]);

  const switchToggle = (winType: WinTypeSelection) => {
    setSelected(winType);
  };

  if (!currentUser) return null;

  return (
    <MainLayout limitItemWidths>
      <div
        className={`aboutUsTypographyReset`}
        style={{
          gridColumn: '1 / 8',
        }}
      >
        <NextSeo
          title="My Wins | Win Epic Prizes | DAYMADE"
          description="Check out what prizes and treats you have won."
          openGraph={{
            title: 'My Wins | Win Epic Prizes | DAYMADE',
            description: 'Check out what prizes and treats you have won.',
          }}
        />
        {initialized && allClaimed.length === 0 && (
          <AboutUsModule background={'redPastel'} withReducedPadding>
            <AboutUsSiteContainer>
              <div
                className={`aboutUsTypographyBodySmall textAlignCenter ${styles.checkEmailMessage}`}
              >
                Make sure you add{' '}
                <a
                  style={{ textDecoration: 'underline', color: 'inherit' }}
                  href="mailto:win@daymade.co.uk"
                >
                  win@daymade.co.uk
                </a>{' '}
                to your contacts to make sure you don't miss out when we let you
                know that you've won!
                <br />
                <strong>And remember to check your junk mail folder!</strong>
              </div>
            </AboutUsSiteContainer>
          </AboutUsModule>
        )}

        <PageTitle
          heroBackgroundImage={`${ASSET_PREFIX}/images/prizes/hero-background.svg`}
          rebrandDm2025
        >
          My Wins
        </PageTitle>

        <AboutUsModule background={'grey'} withNoPadding>
          <AboutUsSiteContainer>
            <p className={styles.subtitle}>
              Everything you've won, in one place.
            </p>
            <div className={styles.winSelectors}>
              {winTypes.map((winType, i) => {
                return (
                  <div className={styles.selectorWrapper} key={i}>
                    <button
                      onClick={() => switchToggle(winType)}
                      className={`${styles.selector} ${selected.type === winType.type ? styles.selected : ''}`}
                    >
                      <span className={styles.selectorTitle}>
                        {winType.title}
                      </span>
                      {winType.type === 'unclaimed' &&
                        unexpiredUnclaimedLength &&
                        unexpiredUnclaimedLength > 0 && (
                          <span className={styles.unclaimedCount}>
                            {unexpiredUnclaimedLength}
                          </span>
                        )}
                    </button>
                    <div
                      className={`${styles.underline} ${i === 0 ? styles.underlineLeft : i === 1 ? styles.underlineRight : ''} ${selected.type === winType.type ? styles.selected : ''}`}
                    />
                  </div>
                );
              })}
            </div>
          </AboutUsSiteContainer>
        </AboutUsModule>

        <AboutUsModule background={'white'}>
          <AboutUsSiteContainer>
            <div>
              <div className="aboutUsTypographyHeaderMedium textAlignCenter">
                {selected.title}
              </div>
              {selected.subtitle && (
                <p className={styles.winSubtitle}>{selected.subtitle}</p>
              )}
            </div>
            {unclaimedPrizeWins === 'ERROR' || claimedPrizeWins === 'ERROR' ? (
              <div className={styles.centerMargin}>
                Sorry, there was a problem loading your{' '}
                {unclaimedPrizeWins === 'ERROR'
                  ? claimedPrizeWins === 'ERROR'
                    ? ''
                    : ' unclaimed '
                  : ' claimed '}{' '}
                prizes. Please try again later.
              </div>
            ) : (
              ''
            )}
            {unclaimedPrizeWins === 'LOADING' ||
            claimedPrizeWins === 'LOADING' ? (
              <div className={styles.centerMargin}>
                <PlainLoadingSpinner />
              </div>
            ) : (
              ''
            )}
            {selected.type === 'unclaimed' && (
              <>
                <div className={styles.winsList}>
                  {allUnclaimed.map((prize) => {
                    const prizeIsPromo = isUnclaimedPromoPrizeReward(prize);
                    const prizeType = prizeIsPromo
                      ? 'CONSOLATION_PRIZE'
                      : prize?.prizeStructureCode === MAIN_PRIZE_STRUCTURE_CODE
                        ? 'MAIN_DRAW'
                        : prize.prizeTierCode === 'MONTHLY_GIVEAWAY'
                          ? 'MONTHLY_GIVEAWAY'
                          : 'TOKEN_TOWN';

                    return (
                      <UnclaimedPrize
                        key={prize?.id}
                        prizeWinId={prize.id}
                        drawName={!prizeIsPromo ? prize.drawName : ''}
                        drawId={!prizeIsPromo ? prize.drawId : undefined}
                        prizeTierCode={
                          !prizeIsPromo ? prize.prizeTierCode : prize.code
                        }
                        prizeImageLocation={
                          !prizeIsPromo
                            ? prize?.drawPrizeImageLocation || ''
                            : ''
                        }
                        prizeType={prizeType}
                        createdAt={prize.createdAt}
                        expiresAt={prize.expiresAt!}
                        drawPrizeSlug={
                          !prizeIsPromo
                            ? prize.drawPrizeSlug || undefined
                            : undefined
                        }
                        onPrizeClaimed={getPrizeWinsAndRewards}
                        prizeName={prizeIsPromo ? prize.prizeTitle || '' : ''}
                        showExpiry={true}
                      />
                    );
                  })}
                </div>
                {Array.isArray(unclaimedPrizeWins) &&
                  Array.isArray(unclaimedPromoPrizeRewards) &&
                  allUnclaimed.length === 0 && <NothingHereMessage />}
              </>
            )}
            {selected.type === 'claimed' && (
              <>
                <div className={styles.winsList}>
                  {allClaimed.map((prize) => {
                    const prizeIsPromo = isUnclaimedPromoPrizeReward(prize);
                    return (
                      <ClaimedPrize
                        key={prize.id}
                        className={styles.prizeGridItem}
                        prizeWinId={prize.id}
                        prizeStructureCode={
                          !prizeIsPromo ? prize.prizeStructureCode : ''
                        }
                        chosenAt={!prizeIsPromo ? prize.chosenAt : null}
                        chosenPrizeName={
                          !prizeIsPromo
                            ? prize.chosenPrizeName
                            : prize.prizeTitle
                        }
                        prizeTierCode={
                          !prizeIsPromo ? prize.prizeTierCode : prize.code
                        }
                        createdAt={prize.createdAt}
                        chosenPrizeImageLocation={
                          !prizeIsPromo
                            ? prize.chosenPrizeImageLocation
                            : prize.prizeImageLocation
                        }
                        drawName={!prizeIsPromo ? prize.drawName : undefined}
                        showDrawInfo={true}
                      />
                    );
                  })}
                </div>
                {Array.isArray(claimedPrizeWins) &&
                  Array.isArray(claimedPromoPrizeRewards) &&
                  allClaimed.length === 0 && <NothingHereMessage />}
              </>
            )}
          </AboutUsSiteContainer>
        </AboutUsModule>

        <div className={styles.backLinkWrapper}>
          <Link href={getAccountLink()} passHref>
            <BackButton />
          </Link>
        </div>
      </div>
    </MainLayout>
  );
});

export default MyWins;
